import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, timer } from 'rxjs';
import { MessageService } from 'primeng/api';
import { AuthenticationService } from 'src/core/services/authentication.service';
import { NotificationService } from 'src/app/search-assign/shared/services/notification.service';
import { DistributionService } from './distribution.service';
import { DistributionUiService } from './distribution-ui.service';
import { PublishConflictResponse, PublishJobState, PublishStatusResponse } from '../models/publish-job.model';

const POLL_INTERVAL_MS = 10000;
const POLL_INTERVAL_BACKOFF_MS = 10000;
const POLL_INTERVAL_MAX_BACKOFF_MS = 30000;
const RESUMABLE_JOB_MAX_AGE_MS = 2 * 60 * 60 * 1000;
const FAILED_TOAST_KEY = 'publish-job-failed';

// Single-flight guard + resumable async polling for the Publish Distributions job.
@Injectable({
  providedIn: 'root'
})
export class PublishJobService {
  private busySubject = new BehaviorSubject<boolean>(false);
  private jobStateSubject = new BehaviorSubject<PublishJobState | null>(null);
  private pollSub?: Subscription;
  private consecutiveErrors = 0;

  isPublishing$: Observable<boolean> = this.busySubject.asObservable();
  jobState$: Observable<PublishJobState | null> = this.jobStateSubject.asObservable();

  constructor(
    private distributionService: DistributionService,
    private distributionUiService: DistributionUiService,
    private notificationService: NotificationService,
    private messageService: MessageService,
    private authService: AuthenticationService
  ) {
    this.authService.currentUser.subscribe((user: any) => {
      if (!user) {
        this.clearInMemory();
      }
    });

    window.addEventListener('storage', this.onStorageEvent);
  }

  isPublishing(): boolean {
    return this.busySubject.value;
  }

  publish(distributionIds: number[]): void {
    this.startPublish(distributionIds, false);
  }

  publishAndNotify(distributionIds: number[]): void {
    this.startPublish(distributionIds, true);
  }

  private startPublish(distributionIds: number[], notifyOnComplete: boolean): void {
    if (this.busySubject.value) {
      return;
    }
    // Set + persist synchronously before the HTTP call resolves so a double-click can't fire twice
    // and other tabs pick up the lock via the 'storage' event during the in-flight window.
    this.busySubject.next(true);
    const startedAtUtc = new Date().toISOString();
    const placeholder: PublishJobState = {
      jobId: '',
      distributionIds,
      startedAtUtc,
      status: 'processing',
      error: null
    };
    this.persist(placeholder);

    const publishRequest = notifyOnComplete
      ? this.distributionService.PublishAndSendDistributionNotificationEmails(distributionIds)
      : this.distributionService.PublishDistributions(distributionIds);

    publishRequest.subscribe({
      next: (res) => {
        const state: PublishJobState = { ...placeholder, jobId: res.jobId };
        this.persist(state);
        this.poll(state.jobId);
      },
      error: (err: HttpErrorResponse) => {
        if (err.status === 409) {
          const body = err.error as PublishConflictResponse;
          const state: PublishJobState = { ...placeholder, jobId: body.activeJobId };
          this.persist(state);
          this.notificationService.showInfo(body?.Message || 'A publish job is already running. Try again once it finishes.');
          this.poll(state.jobId);
          return;
        }

        // Server never accepted the job (400 etc): drop the placeholder so other tabs unlock too.
        this.clearStoredJob();
        this.busySubject.next(false);
        const message = err.error?.Message || err.error?.message || 'Failed to publish distributions. Please try again.';
        this.notificationService.showError(message);
      }
    });
  }

  resumeIfNeeded(): void {
    const key = this.storageKey();
    if (!key) {
      return;
    }

    const raw = localStorage.getItem(key);
    if (!raw) {
      return;
    }

    const state: PublishJobState = JSON.parse(raw);
    const ageMs = Date.now() - new Date(state.startedAtUtc).getTime();

    if (state.status === 'processing' && ageMs < RESUMABLE_JOB_MAX_AGE_MS) {
      this.jobStateSubject.next(state);
      this.busySubject.next(true);
      // Empty jobId is the placeholder written before the initiating tab got the 202 back; wait for the real id via storage event.
      if (state.jobId) {
        this.poll(state.jobId);
      }
    } else {
      localStorage.removeItem(key);
    }
  }

  acknowledgeFailedJob(): void {
    this.clearStoredJob();
  }

  private poll(jobId: string): void {
    this.pollSub?.unsubscribe();
    this.pollSub = this.distributionService.GetPublishStatus(jobId).subscribe({
      next: (res) => {
        this.consecutiveErrors = 0;
        this.handleStatus(jobId, res);
      },
      error: (err: HttpErrorResponse) => this.handlePollError(jobId, err)
    });
  }

  private scheduleNextPoll(jobId: string): void {
    this.pollSub?.unsubscribe();
    this.pollSub = timer(this.currentDelay()).subscribe(() => this.poll(jobId));
  }

  private handleStatus(jobId: string, res: PublishStatusResponse): void {
    const existing = this.jobStateSubject.value;

    if (res.status === 'processing') {
      if (existing) {
        this.persist({ ...existing, status: 'processing' });
      }
      this.scheduleNextPoll(jobId);
      return;
    }

    if (res.status === 'completed') {
      this.busySubject.next(false);
      this.clearStoredJob();
      this.notificationService.showSuccess('Distributions published successfully!');
      this.distributionUiService.emitRefreshData();
      return;
    }

    // failed: kept in storage until the user dismisses the toast (see acknowledgeFailedJob)
    this.busySubject.next(false);
    const failedState: PublishJobState = existing
      ? { ...existing, status: 'failed', error: res.error ?? 'Unknown error' }
      : { jobId, distributionIds: [], startedAtUtc: new Date().toISOString(), status: 'failed', error: res.error ?? 'Unknown error' };
    this.persist(failedState);
    this.messageService.add({
      key: FAILED_TOAST_KEY,
      severity: 'error',
      summary: 'Publish Failed',
      detail: failedState.error ?? 'Unknown error',
      sticky: true
    });
  }

  private handlePollError(jobId: string, err: HttpErrorResponse): void {
    if (err.status === 404) {
      // Job status lives in server memory only; a 404 means it's gone for good, not a transient failure.
      this.busySubject.next(false);
      this.clearStoredJob();
      this.notificationService.showInfo('Publish status is no longer available.');
      this.distributionUiService.emitRefreshData();
      return;
    }

    this.consecutiveErrors++;
    console.error('Error polling publish status:', err);
    this.scheduleNextPoll(jobId);
  }

  private currentDelay(): number {
    if (this.consecutiveErrors >= 6) {
      return POLL_INTERVAL_MAX_BACKOFF_MS;
    }
    if (this.consecutiveErrors >= 3) {
      return POLL_INTERVAL_BACKOFF_MS;
    }
    return POLL_INTERVAL_MS;
  }

  private storageKey(): string | null {
    const userId = this.authService.currentUserValue?.user?.id;
    return userId ? `mpo.publishJob.${userId}` : null;
  }

  private persist(state: PublishJobState): void {
    const key = this.storageKey();
    if (key) {
      localStorage.setItem(key, JSON.stringify(state));
    }
    this.jobStateSubject.next(state);
  }

  private clearStoredJob(): void {
    const key = this.storageKey();
    if (key) {
      localStorage.removeItem(key);
    }
    this.jobStateSubject.next(null);
  }

  private clearInMemory(): void {
    this.pollSub?.unsubscribe();
    this.pollSub = undefined;
    this.consecutiveErrors = 0;
    this.busySubject.next(false);
    this.jobStateSubject.next(null);
  }

  // Fires only for changes made in *other* tabs/windows, which is exactly what cross-tab sync needs.
  private onStorageEvent = (event: StorageEvent): void => {
    const key = this.storageKey();
    if (!key || event.key !== key) {
      return;
    }

    if (!event.newValue) {
      this.clearInMemory();
      return;
    }

    const state: PublishJobState = JSON.parse(event.newValue);
    this.jobStateSubject.next(state);
    this.busySubject.next(state.status === 'processing');

    if (state.status === 'processing' && !this.pollSub && state.jobId) {
      this.poll(state.jobId);
    }
  };
}
